import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, UserPlus, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemberRegister = () => {
  const { societyId } = useParams();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("code") || "";
  
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    flat_number: "",
    flat_area: "",
    carpet_area: "",
    invite_code: inviteCode
  });
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    fetchSociety();
  }, [societyId]);

  const fetchSociety = async () => {
    try {
      const res = await fetch(`${API}/public/society/${societyId}`);
      if (res.ok) {
        setSociety(await res.json());
      } else {
        toast.error("Society not found");
      }
    } catch (error) {
      toast.error("Failed to load society info");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentGiven) {
      toast.error("Please accept the privacy consent to register");
      return;
    }
    setSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        flat_number: formData.flat_number,
        flat_area: formData.flat_area ? parseFloat(formData.flat_area) : 0,
        carpet_area: formData.carpet_area ? parseFloat(formData.carpet_area) : null,
        invite_code: formData.invite_code || null,
        consent_given: true,
        consent_at: new Date().toISOString()
      };

      const res = await fetch(`${API}/societies/${societyId}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success("Registration submitted successfully!");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Registration failed");
      }
    } catch (error) {
      toast.error("Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!society) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Society not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
              Registration Submitted!
            </h2>
            <p className="text-slate-600 mb-4">
              Your registration for <strong>{society.name}</strong> has been submitted.
            </p>
            <p className="text-sm text-slate-500">
              The society committee will review your request and you'll receive an email once approved.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Join {society.name}
          </h1>
          <p className="text-slate-600">{society.address}</p>
          {society.total_flats > 0 && (
            <p className="text-sm text-slate-500">{society.total_flats} flats</p>
          )}
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-700" />
              Member Registration
            </CardTitle>
            <CardDescription>
              Fill in your details to register as a member of this society
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Rajesh Kumar Sharma"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g., rajesh@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g., 9876543210"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="flat_number">Flat Number *</Label>
                  <Input
                    id="flat_number"
                    name="flat_number"
                    value={formData.flat_number}
                    onChange={handleChange}
                    placeholder="e.g., A-101"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="flat_area">Flat Area (sq.ft)</Label>
                  <Input
                    id="flat_area"
                    name="flat_area"
                    type="number"
                    value={formData.flat_area}
                    onChange={handleChange}
                    placeholder="e.g., 850"
                  />
                </div>
              </div>
              
              {inviteCode && (
                <div>
                  <Label htmlFor="invite_code">Invite Code</Label>
                  <Input
                    id="invite_code"
                    name="invite_code"
                    value={formData.invite_code}
                    onChange={handleChange}
                    placeholder="Enter invite code if you have one"
                    className="font-mono"
                  />
                </div>
              )}

              <label className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  className="mt-0.5"
                  data-testid="member-consent-checkbox"
                />
                <span>
                  I consent to ReDevelop collecting my name, contact and flat details to register me as a
                  member of this society (as per DPDPA). My personal data is kept separate from the society's
                  building-asset data. My consent is recorded with a timestamp.
                </span>
              </label>

              <Button 
                type="submit" 
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                disabled={submitting || !consentGiven}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Submit Registration
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Your registration will be reviewed by the society committee.
          You'll receive an email once approved.
        </p>
      </div>
    </div>
  );
};
